import { Alert, Box, Button, Grid, Typography } from "@mui/material";
import type { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import { Channels, type ChannelName } from "../../../channels/types";
import {
  MomentAccordion,
  type MomentAccordionProps,
} from "../../../generic/components/moment-accordion/moment-accordion";
import type { Tags } from "../../../merge-tag/types";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { CopyMomentButton } from "../buttons/copy-moment-button";
import { Channel } from "../channel/channel";

interface ChannelAccordionProps extends Pick<
  MomentAccordionProps,
  "open" | "setOpen" | "onConfirm" | "sx"
> {
  formDisabled: boolean;
  channels: ChannelName[];
  lastFormAction?: "draft" | "publish";
  newsletter?: boolean;
  // Technically this only appears when the moment has been sent so
  // it cannot be in a draft state, but just in case.
  copyDisabled: boolean;
  mergeTags: Tags;
}

export const ChannelAccordion: FC<ChannelAccordionProps> = ({
  open,
  setOpen,
  formDisabled,
  channels,
  onConfirm,
  lastFormAction,
  newsletter,
  sx,
  copyDisabled,
  mergeTags,
}) => {
  const { control, getValues } = useFormContext<MomentFormMoment>();

  useWatch({ control, name: ["channel", "sent"] });
  const [watchedChannel, sent] = getValues(["channel", "sent"]);

  return (
    <MomentAccordion
      id="channelAccordion"
      title="Channel"
      subtitle="Select Your Channel"
      index={1}
      open={open}
      setOpen={setOpen}
      summaryIcon={
        watchedChannel && <ChannelIcon channel={watchedChannel} sx={{ width: 45, height: 45 }} />
      }
      onConfirm={onConfirm}
      disabled={formDisabled}
      sx={{ minWidth: "100%", mb: 3, ...sx }}
      valid={{
        lastFormAction,
        valid: watchedChannel !== null,
      }}
    >
      <Grid container direction="column">
        {sent && watchedChannel && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            slotProps={{
              message: {
                sx: { display: "flex", flexDirection: "row", alignItems: "center" },
              },
              icon: {
                // This is centered as the Duplicate Communication button
                // is designed with an invisible padding so it looks centered
                sx: {
                  display: "flex",
                  alignItems: "center",
                },
              },
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                mr: 2,
              }}
            >
              <Typography variant="body2">
                This Communication is set to send via {Channels[watchedChannel].buttonText}. To send
                this Communication via a different channel, duplicate the Communication and set a
                new channel.
              </Typography>
            </Box>

            <CopyMomentButton
              formSaveState={{
                formDisabled: {
                  "draft-button": copyDisabled,
                },
              }}
              location="channel-accordian"
            >
              <Button
                variant="text"
                color="info"
                data-analytics-id="moment-form-copy-button-channel-accordian"
              >
                Duplicate Communication
              </Button>
            </CopyMomentButton>
          </Alert>
        )}
        <Grid container spacing={7}>
          <Grid size={{ md: 4 }}>
            <h2>Channel</h2>
            To begin, please select the channel you would like to send your Communication through.
            <Alert
              severity="info"
              icon={false}
              sx={{
                mt: 2,
              }}
            >
              <Typography variant="body2">
                This cannot be changed {sent ? "because" : "once"} the Communication has been sent
                for the first time.
              </Typography>
            </Alert>
          </Grid>
          <Grid
            size={{ md: 8 }}
            sx={{
              display: "flex",
              flexDirection: "row",
              borderRadius: 1,
            }}
          >
            <Channel
              channels={channels}
              disabled={formDisabled}
              newsletter={newsletter}
              mergeTags={mergeTags}
            />
          </Grid>
        </Grid>
      </Grid>
    </MomentAccordion>
  );
};
